import React from 'react'

import { CheckCircleOutline } from '@mui/icons-material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, Checkbox, Chip, FormControlLabel, IconButton, Stack, Typography } from '@mui/material'

import Card from '../../../../../../../../../../shared/components/Card/Card.component'
import Link from '../../../../../../../../../../shared/components/Link/Link.component'
import Table from '../../../../../../../../../../shared/components/Table/Table.component'
import ReleaseItemButton from '../ReleaseItemButton/ReleaseItemButton.component'

import date, { dateTimeFormats } from '../../../../../../../../../../shared/functions/Date/date.functions'
import { formatAdminLockerName } from '../../../../../../../../../../shared/functions/Locker/locker.functions'
import status from '../../../../../../../../../../shared/functions/Status/status.dictionaries'
import { getStatusColor } from '../../../../../../../../../../shared/functions/Status/status.functions'
import {
  formatMoney,
  joinElementsWithBullet,
  joinStringsWithBullet,
  truncateString
} from '../../../../../../../../../../shared/functions/String/string.functions'
import theme from '../../../../../../../../../../shared/styles/themes/default.theme'
import { ItemRequestCardProps } from '../../OrderDetails.types'
import styles from './ItemRequestCard.styles'

const ItemRequestCard: React.FC<ItemRequestCardProps> = (props) => {
  const { itemRequest } = props
  const { item, lockerDetails } = itemRequest

  const isItemRequestAcceptedByTraveler = itemRequest.status.name === status.itemRequestStatus.acceptedByTraveler
  const isItemRequestDeniedByTraveler = itemRequest.status.name === status.itemRequestStatus.deniedByTraveler

  const subtitle = joinElementsWithBullet([
    item.asin,
    <Typography variant="caption" sx={styles.availabilityStatus(item.availabilityStatus)}>
      {item.availabilityStatus}
    </Typography>
  ]) as string

  const link = (
    <Link href={item.url} target="_blank" rel="noopener noreferrer" sx={styles.link}>
      View on Amazon
    </Link>
  )

  const linkAndSubtitle = joinElementsWithBullet([link, subtitle]) as string

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code)
  }

  const getLockerStatusChipProps = (lockerStatus: string) => {
    const statusLower = lockerStatus.toLowerCase()

    switch (statusLower) {
      case 'pending':
      case 'dropoff_pending':
        return {
          label: 'Dropoff Pending',
          color: theme.palette.warning.main,
          backgroundColor: theme.palette.warning.light
        }
      case 'pickup_pending':
        return {
          label: 'Pickup Pending',
          color: theme.palette.info.main,
          backgroundColor: theme.palette.info.light
        }
      case 'completed':
        return {
          label: 'Completed',
          color: theme.palette.success.main,
          backgroundColor: theme.palette.success.light
        }
      case 'cancelled':
        return {
          label: 'Cancelled',
          color: theme.palette.error.main,
          backgroundColor: theme.palette.error.light
        }
      default:
        return {
          label: lockerStatus,
          color: theme.palette.grey[700],
          backgroundColor: theme.palette.grey[200]
        }
    }
  }

  return (
    <Card
      header={{
        left: {
          component: (
            <Box sx={styles.productImageContainer}>
              <img src={item.pictureUrl} style={styles.productImage} alt="Product" />
            </Box>
          ),
          title: truncateString(item.productName, 85),
          subtitle: linkAndSubtitle
        },
        right: {
          component: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label={itemRequest.status.displayName} color={getStatusColor(itemRequest.status.name)} />

              {(isItemRequestAcceptedByTraveler || isItemRequestDeniedByTraveler) && (
                <ReleaseItemButton itemRequestId={itemRequest.id} itemRequest={itemRequest} />
              )}
            </Box>
          )
        }
      }}
      body={{
        content: (
          <>
            <Box sx={styles.contentSection}>
              <Table
                title="Item Details"
                columns={[
                  { key: 'country', label: 'Country' },
                  { key: 'asin', label: 'ASIN' },
                  { key: 'quantity', label: 'Quantity' },
                  { key: 'weight', label: 'Weight' },
                  { key: 'price', label: 'Latest Amazon Price' }
                ]}
                data={[
                  {
                    country: item.sourceCountry.name,
                    asin: item.asin,
                    quantity: item.quantity,
                    weight: `${item.weight} ${item.weightUnit ? item.weightUnit : 'lbs'}`,
                    price: formatMoney(item.latestPriceOnAmazon, item.currency)
                  }
                ]}
              />
            </Box>

            <Box sx={styles.contentSection}>
              <Table
                title="Price Details"
                columns={[
                  { key: 'acceptedPrice', label: 'Accepted Price' },
                  { key: 'paidByShopper', label: 'Paid by Shopper' },
                  { key: 'deliveryFee', label: 'Delivery Fee' },
                  { key: 'tax', label: 'Tax' }
                ]}
                data={[
                  {
                    acceptedPrice: formatMoney(item.acceptedItemPrice, item.currency),
                    paidByShopper: formatMoney(item.pricePaidByShopper, item.currency),
                    deliveryFee: formatMoney(item.deliveryFee, item.currency),
                    tax: formatMoney(item.tax, item.currency)
                  }
                ]}
              />
            </Box>

            <Box sx={styles.contentSection}>
              <Table
                title="Tax Details"
                columns={[
                  { key: 'jurisdiction', label: 'Jurisdiction' },
                  { key: 'rate', label: 'Tax Rate' },
                  { key: 'isTaxed', label: 'Is Taxed' },
                  { key: 'exemptions', label: 'Has Exemptions' }
                ]}
                data={[
                  {
                    jurisdiction: joinStringsWithBullet([
                      item.taxDetails.jurisdiction.state,
                      item.taxDetails.jurisdiction.country.name
                    ]),
                    rate: `${(item.taxDetails.rate * 100).toFixed(1)}%`,
                    isTaxed: item.taxDetails.isTaxed ? 'Yes' : 'No',
                    exemptions: item.taxDetails.conditionalExemptions ? 'Yes' : 'No'
                  }
                ]}
              />
            </Box>

            <Box sx={styles.contentSection}>
              <Table
                title="Traveler Details"
                columns={[
                  { key: 'benefit', label: 'Benefit' },
                  { key: 'initialAmount', label: 'Initial Amount Allocated' },
                  { key: 'finalPrice', label: 'Final Price Committed to Paying' }
                ]}
                data={[
                  {
                    benefit: item.travelerBenefit ? formatMoney(item.travelerBenefit, item.currency) : '-',
                    initialAmount: item.initialAmountAllocatedToTraveler
                      ? formatMoney(item.initialAmountAllocatedToTraveler, item.currency)
                      : '-',
                    finalPrice: item.finalPriceTravelerCommittedToPaying
                      ? formatMoney(item.finalPriceTravelerCommittedToPaying, item.currency)
                      : '-'
                  }
                ]}
              />
            </Box>

            {lockerDetails && (
              <Box sx={styles.contentSection}>
                <Stack spacing={2}>
                  <Table
                    title="Locker Details"
                    columns={[
                      { key: 'location', label: 'Location' },
                      { key: 'locker', label: 'Locker' },
                      { key: 'size', label: 'Size' },
                      { key: 'status', label: 'Status' }
                    ]}
                    data={[
                      {
                        location: joinStringsWithBullet([lockerDetails.lockerBankName, lockerDetails.cityIataCode]),
                        locker: formatAdminLockerName(lockerDetails),
                        size: lockerDetails.sizeId,
                        status: (
                          <Chip
                            label={getLockerStatusChipProps(lockerDetails.status).label}
                            size="small"
                            sx={{
                              backgroundColor: getLockerStatusChipProps(lockerDetails.status).backgroundColor,
                              color: getLockerStatusChipProps(lockerDetails.status).color,
                              fontWeight: 600
                            }}
                          />
                        )
                      }
                    ]}
                  />

                  <Table
                    title="Locker Access"
                    columns={[
                      { key: 'dropoffCode', label: 'Dropoff Code' },
                      { key: 'pickupCode', label: 'Pickup Code' },
                      { key: 'droppedOff', label: 'Dropped Off' },
                      { key: 'pickedUp', label: 'Picked Up' }
                    ]}
                    data={[
                      {
                        dropoffCode: (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">{lockerDetails.dropoffCode}</Typography>
                            <IconButton size="small" onClick={() => handleCopyCode(lockerDetails.dropoffCode)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ),
                        pickupCode: (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">{lockerDetails.pickupCode}</Typography>
                            <IconButton size="small" onClick={() => handleCopyCode(lockerDetails.pickupCode)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ),
                        droppedOff: (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!lockerDetails.dropoffTimestamp}
                                readOnly
                                disabled={!lockerDetails.dropoffTimestamp}
                              />
                            }
                            label={
                              lockerDetails.dropoffTimestamp ? (
                                <Typography variant="caption" color="text.secondary">
                                  {date(lockerDetails.dropoffTimestamp).format(dateTimeFormats.date.medium)}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Not yet
                                </Typography>
                              )
                            }
                          />
                        ),
                        pickedUp: (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!lockerDetails.pickupTimestamp}
                                readOnly
                                disabled={!lockerDetails.pickupTimestamp}
                              />
                            }
                            label={
                              lockerDetails.pickupTimestamp ? (
                                <Typography variant="caption" color="text.secondary">
                                  {date(lockerDetails.pickupTimestamp).format(dateTimeFormats.date.medium)}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Not yet
                                </Typography>
                              )
                            }
                          />
                        )
                      }
                    ]}
                  />
                </Stack>
              </Box>
            )}
          </>
        ),
        additionalContent: {
          labelText: joinStringsWithBullet(['View Price Change History', item.priceChangeDetails.length.toString()]),
          component: (
            <>
              <Table
                columns={[
                  { key: 'change', label: 'Change' },
                  { key: 'oldValue', label: 'Old Value' },
                  { key: 'newValue', label: 'New Value' },
                  { key: 'changeTime', label: 'Change Time' },
                  { key: 'acceptedByShopper', label: 'Accepted by Shopper' }
                ]}
                data={item.priceChangeDetails.map((change) => ({
                  change:
                    change.newValue > change.oldValue ? (
                      <ArrowUpwardIcon color="error" />
                    ) : (
                      <ArrowDownwardIcon color="success" />
                    ),
                  oldValue: formatMoney(change.oldValue, item.currency),
                  newValue: formatMoney(change.newValue, item.currency),
                  changeTime: date(change.changeTime).format(dateTimeFormats.date.long),
                  acceptedByShopper: change.acceptedByShopper ? <CheckCircleOutline color="success" /> : '-'
                }))}
              />
            </>
          )
        }
      }}
    />
  )
}

export default ItemRequestCard
